import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './ArticleDetails.module.css';

const ArticleDetails = () => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const { t, i18n } = useTranslation("global");

  useEffect(() => {
    fetchArticle();
  }, [id, i18n.language]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/articles/${id}`,
        {
          headers: {
            "Accept-Language": i18n.language,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch article');
      }

      const data = await response.json();
      
      if (data.data) {
        setArticle(data.data);
      } else {
        setError('Article not found');
      }
      
    } catch (err) {
      console.error('Error fetching article:', err);
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (err) {
      return dateString;
    }
  };

  const getArticleTitle = (article) => {
    if (i18n.language === 'ar') {
      return article.title_ar || article.title_en || t("articles.noTitle");
    } else {
      return article.title_en || article.title_ar || t("articles.noTitle");
    }
  };

  const getArticleContent = (article) => {
    if (i18n.language === 'ar') {
      return article.details_ar || article.details_en || '';
    } else {
      return article.details_en || article.details_ar || '';
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <h2>{t("articles.errorTitle") || "Error Loading Article"}</h2>
          <p>{error || "Article not found"}</p>
          <Link to="/articles" className={styles.backBtn}>
            {t("articles.backToArticles") || "Back to Articles"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.articleDetailsContainer}>
      <div className={styles.pageHeader}>
        <div className="container">
          <nav className={styles.breadcrumb}>
            <Link to="/" className={styles.breadcrumbLink}>
              {t("header.home") || "Home"}
            </Link>
            <span className={styles.breadcrumbSeparator}> / </span>
            <Link to="/articles" className={styles.breadcrumbLink}>
              {t("articles.title") || "Articles"}
            </Link>
            <span className={styles.breadcrumbSeparator}> / </span>
            <span className={styles.breadcrumbCurrent}>
              {getArticleTitle(article)}
            </span>
          </nav>
        </div>
      </div>

      <div className="container">
        <article className={styles.articleContent}>
          <header className={styles.articleHeaderContent}>
            <h1 className={styles.articleTitle}>
              {getArticleTitle(article)}
            </h1>
            
            <div className={styles.articleMeta}>
              <div className={styles.articleInfo}>
                <span className={styles.articleDate}>
                  {formatDate(article.created_at)}
                </span>
                <span className={styles.articleAuthor}>
                  {t("articles.by") || "by"} {article.author || t("articles.unknownAuthor") || "Unknown Author"}
                </span>
              </div>
            </div>
          </header>

          {article.image && (
            <div className={styles.articleImageContainer}>
              <img
                src={article.image}
                alt={getArticleTitle(article)}
                className={styles.articleImage}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x400?text=Article+Image';
                }}
              />
            </div>
          )}

          <div 
            className={styles.articleBody}
            dangerouslySetInnerHTML={{ __html: getArticleContent(article) }}
          />

          <footer className={styles.articleFooter}>
            <Link to="/articles" className={styles.backToArticlesBtn}>
              {t("articles.backToArticles") || "← Back to Articles"}
            </Link>
          </footer>
        </article>
      </div>
    </div>
  );
};

export default ArticleDetails;
